import { useTranslation } from 'react-i18next';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';

import { Select } from '@/components';

import { ColorPickerAdvanced } from '../controls/ColorPickerAdvanced';
import { FontFamilyPicker } from '../controls/FontFamilyPicker';
import { NumberInput } from '../controls/NumberInput';
import { StyleToggleButton } from '../controls/StyleToggleButton';
import { FONT_WEIGHTS } from '../styleConstants';
import type { LayoutElementStyle } from '../styleTypes';

interface TypographySectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

export function TypographySection({ style, onChange }: TypographySectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  return (
    <div className="space-y-3">
      {/* Font Family */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}typography.font`)}
        </label>
        <FontFamilyPicker value={style.fontFamily} onChange={(v) => onChange({ fontFamily: v })} />
      </div>

      {/* Font Size + Weight — `flex-wrap`: gdy panel jest za wąski żeby zmieścić oba
          obok siebie, „Grubość" spada pod „Rozmiar" zamiast ściskać obie kontrolki
          do nieczytelnej szerokości (§4.12 — layout musi się zawijać, nie przycinać). */}
      <div className="flex flex-wrap gap-2">
        <NumberInput
          value={style.fontSize}
          onChange={(v) => onChange({ fontSize: v })}
          min={8}
          max={120}
          label={t(`${p}common.size`)}
          unit="px"
          showSlider
          className="min-w-[7rem] flex-1"
        />
        <div className="min-w-[7rem] flex-1">
          <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
            {t(`${p}typography.thickness`)}
          </label>
          <Select
            value={style.fontWeight || ''}
            onChange={(e) =>
              onChange({ fontWeight: e.target.value ? Number(e.target.value) : undefined })
            }
            className="px-2 py-1.5 text-xs"
          >
            <option value="">{t(`${p}typography.defaultWeight`)}</option>
            {FONT_WEIGHTS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.value} — {w.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Line Height + Letter Spacing */}
      <div className="flex flex-wrap gap-2">
        <NumberInput
          value={style.lineHeight}
          onChange={(v) => onChange({ lineHeight: v })}
          min={0.8}
          max={3.0}
          step={0.1}
          label={t(`${p}typography.lineHeight`)}
          unit=""
          className="min-w-[7rem] flex-1"
        />
        <NumberInput
          value={style.letterSpacing}
          onChange={(v) => onChange({ letterSpacing: v })}
          min={-5}
          max={10}
          step={0.5}
          label={t(`${p}typography.letterSpacing`)}
          className="min-w-[7rem] flex-1"
          unit="px"
        />
      </div>

      {/* Text Decoration */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}typography.decoration`)}
        </label>
        <div className="flex gap-1">
          {(
            [
              { v: 'none', icon: Type, label: t(`${p}common.none`) },
              { v: 'underline', icon: Underline, label: t(`${p}typography.underline`) },
              { v: 'line-through', icon: Strikethrough, label: t(`${p}typography.lineThrough`) },
            ] as const
          ).map(({ v, icon: Icon, label }) => (
            <StyleToggleButton
              key={v}
              active={(style.textDecoration || 'none') === v}
              onClick={() => onChange({ textDecoration: v })}
              title={label}
            >
              <Icon className="w-3.5 h-3.5" />
            </StyleToggleButton>
          ))}
        </div>
      </div>

      {/* Text Transform */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}typography.textCase`)}
        </label>
        <div className="flex gap-1">
          {(
            [
              { v: 'none', label: 'Aa' },
              { v: 'uppercase', label: 'AA' },
              { v: 'lowercase', label: 'aa' },
              { v: 'capitalize', label: 'Ab' },
            ] as const
          ).map(({ v, label }) => (
            <StyleToggleButton
              key={v}
              active={(style.textTransform || 'none') === v}
              onClick={() => onChange({ textTransform: v })}
            >
              {label}
            </StyleToggleButton>
          ))}
        </div>
      </div>

      {/* Text Align */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}common.alignment`)}
        </label>
        <div className="flex gap-1">
          {(
            [
              { v: 'left', icon: AlignLeft },
              { v: 'center', icon: AlignCenter },
              { v: 'right', icon: AlignRight },
              { v: 'justify', icon: AlignJustify },
            ] as const
          ).map(({ v, icon: Icon }) => (
            <StyleToggleButton
              key={v}
              active={(style.textAlign || 'left') === v}
              onClick={() => onChange({ textAlign: v })}
            >
              <Icon className="w-3.5 h-3.5" />
            </StyleToggleButton>
          ))}
        </div>
      </div>

      {/* Text Color */}
      <ColorPickerAdvanced
        color={style.textColor}
        onChange={(c) => onChange({ textColor: c })}
        opacity={style.textOpacity}
        onOpacityChange={(o) => onChange({ textOpacity: o })}
        label={t(`${p}typography.textColor`)}
      />
    </div>
  );
}
